import { ReactElement, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { APIProvider } from '@vis.gl/react-google-maps';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import DeviceSelection from '../components/DeviceSelection';
import FileUploadBar from '../components/FileUploadBar';
import {
  ActionData,
  DeviceLocation,
  DeviceType,
  ExtendedDeviceAttributes,
  Group
} from '../constants/telosair-interfaces';
import { getBase64 } from '../_helpers/globalHelper';
import { createGroup, editGroup } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import { TOAST_MESSAGES } from '../constants/global-constants';
import CustomInputBox from '../controls/CustomInputBox';
import CustomRadioButtonGroup from '../controls/CustomRadioButtonGroup';
import RHFMap from '../components/GoogleMap/rhfMap';

type FORMDATA = {
  groupName: string;
  type: DeviceType;
};

const options = [DeviceType.INDOOR, DeviceType.OUTDOOR];
type AddGroupModalProps = {
  handleClose: (newData?: boolean) => void;
  actionData?: ActionData<Group>;
};

/**
 * Modal - AddGroupModal.
 * @param props - Properties of AddGroupModal.
 * @returns ReactElement.
 */
export default function AddGroupModal(props: AddGroupModalProps): ReactElement {
  const { devices: deviceList } = useData();
  const { handleClose, actionData } = props;
  const [newDevicesWithLocation, setNewDevicesWithLocation] = useState<DeviceLocation>({});
  const [floorMap, setFloorMap] = useState('');
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<ExtendedDeviceAttributes[]>([]);
  const [coordinates] = useState('(40.78, -74.18)');

  const methods = useForm<FORMDATA>({
    mode: 'all',

    defaultValues: useMemo(() => {
      const markedLocation: Record<string, { lat: number; lng: number; deviceType: string }> = {};

      // Convert from array to object format
      actionData?.data?.sensorDetailsList?.forEach((item) => {
        markedLocation[item.id] = {
          lat: item.y,
          lng: item.x,
          deviceType: DeviceType.OUTDOOR
        };
      });

      return {
        groupName: actionData?.data?.groupName || '',
        coordinates: actionData?.data?.sensorDetailsList?.map((item) => ({
          id: item.id,
          deviceName: item.deviceName,
          isMarked: true,
          position: {
            lat: item.y,
            lng: item.x
          }
        })),
        type:
          actionData?.data?.floorPlan === DeviceType.OUTDOOR
            ? DeviceType.OUTDOOR
            : DeviceType.INDOOR,
        markedLocation
      };
    }, [actionData])
  });

  useEffect(() => {
    if (actionData?.type === 'edit') {
      const markedLocation: DeviceLocation = {};

      // Convert from array to object format
      actionData?.data?.sensorDetailsList?.forEach((item) => {
        markedLocation[item.id] = {
          x: item.x,
          y: item.y
        };
      });
      setNewDevicesWithLocation(markedLocation);
    }
  }, [actionData]);

  const { handleSubmit, watch } = methods;
  const deviceType = watch('type');

  /**
   * Handles file uploads.
   * @param file - The uploaded file object.
   */
  const handleFileChange = async (file: File): Promise<void> => {
    const base64Data = await getBase64(file);
    if (base64Data) {
      setFloorMap(base64Data.toString());
    }
  };

  /**
   * Handles group creation.
   * @param formData - Formdata.
   */
  const handleCreateGroup = async (formData: FORMDATA): Promise<void> => {
    // Log form data
    const { groupName } = formData;
    try {
      const sensorPositionMap = Object.fromEntries(
        Object.entries(newDevicesWithLocation).filter(([id]) => {
          const device = devices.find((dev) => dev.id === Number(id));
          return device && device.deviceType === deviceType;
        })
      );

      setLoading(true);
      if (actionData?.type === 'edit') {
        const editData = {
          groupName,
          sensorPositionMap,
          floorPlan: deviceType === DeviceType.OUTDOOR ? DeviceType.OUTDOOR : floorMap || 'default',
          groupId: actionData?.data.groupId
        };
        await editGroup(editData);
        toast.success(TOAST_MESSAGES.edited);
      } else {
        const data = {
          groupName,
          sensorIdVsPositionMap: sensorPositionMap,
          filePath: deviceType === DeviceType.OUTDOOR ? DeviceType.OUTDOOR : floorMap || 'default'
        };
        await createGroup(data);
        toast.success(TOAST_MESSAGES.created);
      }
      setLoading(false);
      handleClose(true);
    } catch (e) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error('Error:', e); // Log error
      toast.error(TOAST_MESSAGES.error);
    }
  };

  useEffect(() => {
    if (actionData?.type === 'edit') {
      const grouppedDevices: DeviceLocation = {} as DeviceLocation;
      actionData.data?.sensorDetailsList?.forEach((item) => {
        grouppedDevices[item.id] = { x: item.x, y: item.y };
      });
      const newDeviceList: ExtendedDeviceAttributes[] = deviceList.map((item) => ({
        ...item,
        active: !!grouppedDevices[item.id]
      }));
      setDevices(newDeviceList);
      setNewDevicesWithLocation(grouppedDevices);
      setFloorMap(actionData.data.floorPlan);
    } else {
      const newDeviceList: ExtendedDeviceAttributes[] = deviceList.map((item) => ({
        ...item,
        active: false
      }));
      setDevices(newDeviceList);
    }
  }, [actionData, deviceList, deviceType]);

  /**
   * Handles the selection of a device by updating its active status.
   * @param id - The ID of the device to be updated.
   * @param selectionStatus - The new selection status of the device (active or inactive).
   */
  const handleDeviceSelection = (id: number, selectionStatus: boolean): void => {
    setDevices(
      devices.map((device) => {
        if (device.id === id) {
          return { ...device, active: selectionStatus };
        }
        return device;
      })
    );
    if (!selectionStatus) {
      setNewDevicesWithLocation((prevLocations) => {
        const updatedLocations = { ...prevLocations };
        delete updatedLocations[id];
        return updatedLocations;
      });
    }
  };

  const isEditing = actionData?.type === 'edit';
  const selectedDevices = useMemo(() => devices.filter((dev) => dev.active), [devices]);

  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">Create Group</div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        Create custom group of devices
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form className="relative" onSubmit={handleSubmit(handleCreateGroup)}>
          <CustomInputBox
            idname="groupName"
            label="Group Name"
            validationObject={{ required: 'Please enter the group name.' }}
          />
          {!isEditing ? (
            <CustomRadioButtonGroup<string>
              idName="type"
              options={options.map((option) => ({
                id: option,
                name: option === DeviceType.INDOOR ? 'Indoor' : 'Outdoor'
              }))}
              label="Type"
            />
          ) : (
            <div>
              <div className="block text-gray-500 dark:text-gray-500 text-base font-medium mt-4">
                Type
              </div>
              <label
                htmlFor="radio-deviceType"
                className="inline-flex items-center mt-2 text-base font-normal text-gray-300 dark:text-gray-300 capitalize opacity-60 cursor-not-allowed"
              >
                <input
                  type="radio"
                  id="radio-deviceType"
                  value={deviceType}
                  checked
                  className="form-radio text-primary-600 h-5 w-5 bg-container-500 mr-2 cursor-not-allowed"
                />
                {deviceType?.toLowerCase()}
              </label>
            </div>
          )}
          <DeviceSelection
            deviceType={deviceType}
            deviceDataArray={devices}
            handleSelect={handleDeviceSelection}
          />

          {deviceType === DeviceType.INDOOR && (
            <FileUploadBar
              idname="fileUpload"
              selectedDevices={selectedDevices}
              onFileChange={handleFileChange}
              newDevicesWithLocation={newDevicesWithLocation}
              setNewDevicesWithLocation={setNewDevicesWithLocation}
              fileData={floorMap}
              isEditing={isEditing}
            />
          )}
          {deviceType === DeviceType.OUTDOOR && (
            <div className="mt-4">
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                <RHFMap
                  defaultCenter={coordinates}
                  name="coordinates"
                  handleSelect={handleDeviceSelection}
                  selectedDevices={selectedDevices}
                  newDevicesWithLocation={newDevicesWithLocation}
                  setNewDevicesWithLocation={setNewDevicesWithLocation}
                />
              </APIProvider>
            </div>
          )}
          <div
            style={{ width: 'w-full' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div className="w-full">
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose()}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              buttonType="submit"
              name={actionData?.type === 'edit' ? 'Save Changes' : 'Create Group'}
              isBlockButton
              loading={loading}
              disabled={loading}
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
